// The page layout: the theme plus the ordered list of sections.
import { normalizeSectionStyle, normalizeTheme, SectionStyle, Theme } from '../theme/theme';
import type { Photo } from './index';

export const BUILT_IN_TYPES = ['bio', 'music', 'shows', 'members', 'gallery', 'contact'] as const;
export const CUSTOM_TYPES = ['text', 'photoText', 'video', 'photos', 'buttons', 'newsletter'] as const;

export type BuiltInType = (typeof BUILT_IN_TYPES)[number];
export type CustomType = (typeof CUSTOM_TYPES)[number];
export type SectionType = BuiltInType | CustomType;

export interface LinkButton {
  label: string;
  url: string;
}

export interface Section {
  id: string;
  type: SectionType;
  title?: string;
  hidden?: boolean;
  // Custom section content (only the fields its type uses are set).
  text?: string;
  image?: string;
  imageSide?: 'left' | 'right';
  url?: string;
  photos?: Photo[];
  buttons?: LinkButton[];
  /** Newsletter: Kit form number, and the button's text. */
  formId?: string;
  buttonLabel?: string;
  /** Newsletter: show as a pop-up, on the page, or both; pop-up delay in seconds. */
  display?: 'popup' | 'inline' | 'both';
  popupDelay?: number;
  /** Optional box styling for the section. */
  style?: SectionStyle;
}

/** The photo header at the top of the page. */
export interface HeaderSettings {
  image?: string;
  tagline?: string;
  /** Optional extra button, e.g. "Listen now". */
  buttonLabel?: string;
  buttonUrl?: string;
  /** Show a "Next show" button when there's an upcoming show. */
  showNextShow: boolean;
  /** Show the bar of links to each section. */
  showLinks: boolean;
  height: 'tall' | 'medium' | 'short';
}

export const DEFAULT_HEADER: HeaderSettings = { showNextShow: true, showLinks: true, height: 'tall' };

export function normalizeHeader(input: unknown): HeaderSettings {
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const header: HeaderSettings = { ...DEFAULT_HEADER };
  if (typeof raw.image === 'string' && raw.image.startsWith('/') && !raw.image.startsWith('//')) header.image = raw.image;
  if (typeof raw.tagline === 'string') header.tagline = raw.tagline;
  if (typeof raw.buttonLabel === 'string' && raw.buttonLabel.trim()) header.buttonLabel = raw.buttonLabel.trim();
  if (typeof raw.buttonUrl === 'string' && raw.buttonUrl.trim()) header.buttonUrl = raw.buttonUrl.trim();
  if (typeof raw.showNextShow === 'boolean') header.showNextShow = raw.showNextShow;
  if (typeof raw.showLinks === 'boolean') header.showLinks = raw.showLinks;
  if (raw.height === 'tall' || raw.height === 'medium' || raw.height === 'short') header.height = raw.height;
  return header;
}

export interface Layout {
  theme: Theme;
  header: HeaderSettings;
  sections: Section[];
}

export const SECTION_TYPE_INFO: Record<SectionType, { label: string; defaultTitle?: string; description?: string }> = {
  bio: { label: 'Bio' },
  music: { label: 'Music', defaultTitle: 'MUSIC' },
  shows: { label: 'Shows' },
  members: { label: 'Band members', defaultTitle: 'BAND MEMBERS' },
  gallery: { label: 'Gallery', defaultTitle: 'GALLERY' },
  contact: { label: 'Contact', defaultTitle: 'CONTACT US' },
  text: { label: 'Text', defaultTitle: 'NEW SECTION', description: 'A heading and paragraphs' },
  photoText: { label: 'Photo + text', defaultTitle: 'NEW SECTION', description: 'A photo beside some text' },
  video: { label: 'Video', defaultTitle: 'VIDEO', description: 'One YouTube video or Bandcamp player' },
  photos: { label: 'Photo gallery', defaultTitle: 'PHOTOS', description: 'Another grid of photos' },
  buttons: { label: 'Buttons', defaultTitle: 'LINKS', description: 'Links for merch, pre-saves, tickets…' },
  newsletter: { label: 'Newsletter signup', defaultTitle: 'JOIN THE MAILING LIST', description: 'An email signup box (Kit)' },
};

export const isBuiltIn = (type: SectionType): type is BuiltInType => (BUILT_IN_TYPES as readonly string[]).includes(type);
const isSectionType = (value: unknown): value is SectionType =>
  typeof value === 'string' && ([...BUILT_IN_TYPES, ...CUSTOM_TYPES] as string[]).includes(value);

export const newSectionId = (type: SectionType, random = Math.random().toString(36).slice(2, 8)) => `${type}-${random}`;

/** A fresh custom section with starter content. */
export function newSection(type: CustomType, title?: string): Section {
  const base: Section = { id: newSectionId(type), type, title: title || SECTION_TYPE_INFO[type].defaultTitle };
  switch (type) {
    case 'text':
      return { ...base, text: 'Write something here.' };
    case 'photoText':
      return { ...base, text: 'Write something here.', imageSide: 'left' };
    case 'photos':
      return { ...base, photos: [] };
    case 'buttons':
      return { ...base, buttons: [] };
    case 'newsletter':
      return { ...base, text: 'Get show announcements and new music first. No spam, unsubscribe anytime.', buttonLabel: 'Subscribe', display: 'inline' };
    default:
      return base;
  }
}

/**
 * Cleans up a layout read from a file: drops unknown section types and
 * duplicate ids, and makes sure every built-in section appears exactly once,
 * so content can never be lost by a broken layout file.
 */
export function normalizeLayout(input: unknown): Layout {
  const raw = (input && typeof input === 'object' ? input : {}) as { theme?: unknown; header?: unknown; sections?: unknown };
  const seenIds = new Set<string>();
  const seenBuiltIns = new Set<BuiltInType>();
  const sections: Section[] = [];

  (Array.isArray(raw.sections) ? raw.sections : []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const section = item as Section;
    if (!isSectionType(section.type)) return;
    if (isBuiltIn(section.type)) {
      if (seenBuiltIns.has(section.type)) return;
      seenBuiltIns.add(section.type);
    }
    let id = typeof section.id === 'string' && section.id ? section.id : newSectionId(section.type);
    while (seenIds.has(id)) id = newSectionId(section.type);
    seenIds.add(id);
    const style = normalizeSectionStyle(section.style);
    const clean: Section = { ...section, id };
    if (style) clean.style = style;
    else delete clean.style;
    sections.push(clean);
  });

  BUILT_IN_TYPES.forEach((type) => {
    if (!seenBuiltIns.has(type)) sections.push({ id: type, type, title: SECTION_TYPE_INFO[type].defaultTitle });
  });

  return { theme: normalizeTheme(raw.theme), header: normalizeHeader(raw.header), sections };
}
